import Clarity from '@microsoft/clarity';

/**
 * Clarity project ID. Stored in the environment (VITE_CLARITY_PROJECT_ID)
 * so it can be configured per environment without code changes.
 * When unset, all Clarity calls become no-ops so the app keeps working.
 */
const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID;

/**
 * localStorage key that records whether the user has accepted the mandatory
 * Clarity analytics permissions. Beta access is gated on this consent.
 */
const CLARITY_CONSENT_KEY = 'clarity-consent';

export interface ClarityUser {
  id?: string;
  email?: string;
  name?: string;
}

/**
 * True when a Clarity project ID has been configured.
 */
export const isClarityEnabled = (): boolean => Boolean(CLARITY_PROJECT_ID);

/**
 * True when the user has accepted the mandatory Clarity analytics permissions
 * on this device/browser.
 */
export const hasClarityConsent = (): boolean =>
  localStorage.getItem(CLARITY_CONSENT_KEY) === 'granted';

/**
 * Initializes Clarity. Called once when the app boots.
 *
 * TEMP (no-consent-gate experiment): analytics storage is granted
 * unconditionally at boot because the consent banner is unmounted (see
 * App.tsx) — this keeps multi-page sessions/funnels joined. Revert to the
 * consent-gated grant (only when hasClarityConsent()) when the banner is
 * re-enabled.
 */
export function initClarity(): void {
  if (!isClarityEnabled()) {
    console.warn('Clarity project ID not configured; skipping Clarity init.');
    return;
  }
  Clarity.init(CLARITY_PROJECT_ID as string);
  setClarityConsent(true);
}

/**
 * Low-level consent setter. Analytics storage is granted/denied as requested;
 * ad storage is always denied — Clarity is used for product analytics only.
 */
export function setClarityConsent(consentGiven: boolean): void {
  if (!isClarityEnabled()) return;
  Clarity.consentV2({
    ad_Storage: 'denied',
    analytics_Storage: consentGiven ? 'granted' : 'denied',
  });
}

/**
 * Grants analytics consent and persists the choice. The localStorage flag is
 * written unconditionally (so the consent gate stays dismissed across visits
 * even when Clarity is not configured); the Clarity API calls are only made
 * when a project ID is present. init() is idempotent, so it's safe to call when
 * Clarity already booted. Invoked from the mandatory consent banner — without
 * this, the app stays gated.
 */
export function grantClarityConsent(): void {
  localStorage.setItem(CLARITY_CONSENT_KEY, 'granted');
  if (!isClarityEnabled()) return;
  Clarity.init(CLARITY_PROJECT_ID as string);
  setClarityConsent(true);
}

/**
 * Revokes analytics consent and clears the stored choice.
 */
export function revokeClarityConsent(): void {
  localStorage.removeItem(CLARITY_CONSENT_KEY);
  setClarityConsent(false);
}

/**
 * Links the current session to a specific user. Clarity hashes the customId
 * on the client before sending it, so it's safe to send the internal user id.
 * Per Clarity docs, this should be called on every page.
 */
export function identifyClarityUser(user?: ClarityUser | null): void {
  if (!isClarityEnabled() || !user?.id) return;
  const friendlyName = user.name || user.email || '';
  Clarity.identify(user.id, undefined, undefined, friendlyName);
}

/**
 * Tags the current session with the visited page and re-identifies the current
 * user (loaded from localStorage) so the recording stays attributed correctly
 * across client-side route changes.
 */
export function trackClarityPage(pageName: string): void {
  if (!isClarityEnabled()) return;
  Clarity.setTag('page', pageName);

  let storedUser: ClarityUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    storedUser = {};
  }
  identifyClarityUser(storedUser);
}

/**
 * Records a custom event. Shows up alongside Smart events in Clarity filters,
 * dashboards and recordings.
 */
export function trackClarityEvent(eventName: string): void {
  if (!isClarityEnabled()) return;
  Clarity.event(eventName);
}

/**
 * Upgrades the priority of the current session for recording, useful for
 * sessions with key interactions (e.g. reaching the publish step).
 */
export function upgradeClaritySession(reason: string): void {
  if (!isClarityEnabled()) return;
  Clarity.upgrade(reason);
}
